import { ReactNode } from 'react';

import { cn } from '@/lib/utils';
import { isKeyEnter } from '@/util/events';

const INTERACTIVE_SELECTOR = 'button,a,input,textarea,select,[role="button"],[role="link"]';

const isFromInteractiveChild = (target: EventTarget | null, boundary: Element) => {
  if (!(target instanceof Element)) {
    return false;
  }
  if (target instanceof HTMLElement && target.isContentEditable) {
    return true;
  }
  const hit = target.closest(INTERACTIVE_SELECTOR);
  return hit !== null && hit !== boundary;
};

interface TurnBlockProps {
  index: number;
  isActive: boolean;
  couldShowAmend: boolean;
  ariaLabel: string;
  onSelect: (index: number) => void;
  children: ReactNode;
}

export const TurnBlock = ({
  index,
  isActive,
  couldShowAmend,
  ariaLabel,
  onSelect,
  children,
}: TurnBlockProps) => {
  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (isFromInteractiveChild(e.target, e.currentTarget)) {
      return;
    }
    onSelect(index);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (isFromInteractiveChild(e.target, e.currentTarget)) {
      return;
    }
    if (isKeyEnter(e) || e.key === ' ') {
      e.preventDefault();
      onSelect(index);
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      aria-pressed={isActive}
      aria-label={ariaLabel}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className={cn(
        'group flex flex-col gap-3 px-4 pt-2 transition-colors',
        couldShowAmend && !isActive ? 'pb-12' : 'pb-6',
        isActive ? 'bg-highlight-background' : 'cursor-pointer hover:bg-bg-muted-50'
      )}
    >
      {children}
    </div>
  );
};
